/**
 * Servicio para conectar con el módulo de Distribución del backend.
 * Incluye:
 *   - Coverage por producto (B1.13).
 *   - Validación previa de PO contra cobertura por tienda.
 *   - Backlog de denegaciones.
 *   - Trazabilidad inversa de POs.
 *   - Pendientes denials por producto.
 *   - Widget home compras (B1.15).
 *
 * Las llamadas del agente humano (DistribucionTiendaPage) son del Bloque 2,
 * no entran aquí.
 */

import { apiClient } from "./apiClient";

export interface ProductCoverage {
  unified_product_id: string;
  total_qty_planned: number;
  qty_replenishment_total: number;
  qty_sample_total: number;
  qty_reorder_total: number;
  qty_denial_fulfillment_total: number;
  qty_central_buffer: number;
  stores_covered: string[];
  stores_uncovered: string[];
  pending_denials: number;
  pending_denials_qty: number;
}

export interface PurchaseSuggestion {
  unified_product_id: string;
  abc: "A" | "B" | "C" | null;
  qty_piezas_before_pack: number;
  pack_size: number;
  qty_compra_final: number;
  breakdown_by_store: PurchaseBreakdownPerStore[];
  stores_covered: string[];
  stores_uncovered: string[];
  denials_resolved_ids: number[];
  qty_replenishment_total: number;
  qty_sample_total: number;
  qty_reorder_total: number;
  qty_denial_fulfillment_total: number;
  qty_central_buffer: number;
  qty_excess_moq_distributed: number;
  forecast_monthly_agg: number;
  inv_central: number;
  horizon_months: number;
  buffer_central_weeks: number;
  used_anti_double_counting: boolean;
}

export interface PurchaseBreakdownPerStore {
  store: string;
  qty_replenishment: number;
  qty_sample: number;
  qty_reorder: number;
  qty_denial_fulfillment: number;
  deficit_reason: "replenishment" | "sample" | "reorder" | "denial" | "none";
  rop: number;
  current_stock: number;
}

export interface ValidatePurchaseCoverageResult {
  unified_product_id: string;
  proposed_qty: number;
  required_qty: number;
  ok: boolean;
  shortfall: number;
  alerts: string[];
  suggestion: PurchaseSuggestion;
}

export interface BacklogItem {
  unified_product_id: string;
  pending_denials_count: number;
  pending_denials_qty: number;
  oldest_denial_date: string;
  affected_stores: string[];
}

export interface PendingDenialSummary {
  unified_product_id: string;
  total_qty_denied: number;
  stores: Array<{ store: string; qty: number; days_open: number }>;
}

export interface PoTraceEntry {
  po_id: string;
  po_line_id: string;
  unified_product_id: string;
  total_qty: number;
  links: Array<{
    store_mxp_code: string;
    qty: number;
    deficit_type: string;
    denial_id: number | null;
  }>;
}

export interface HomeWidgetSnapshot {
  total_pending_denials: number;
  total_qty_denied: number;
  top_5_stores_with_deficits: Array<{
    store: string;
    total_qty_denied: number;
  }>;
  top_10_atascados: Array<{
    unified_product_id: string;
    pending_qty: number;
    stores: number;
    oldest_days: number;
  }>;
  diffs_pending_capture: number;
  subcompra_alerts: number;
}

export type PostSyncStepStatus =
  | "running"
  | "completed"
  | "failed"
  | "skipped";

export interface PipelineStepLog {
  step: string;
  status: PostSyncStepStatus;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  stats: Record<string, unknown>;
}

export interface LogLine {
  ts: number;
  level: "log" | "warn" | "error" | "debug" | "verbose";
  context: string;
  message: string;
}

export interface PipelineRunLog {
  sync_run_id: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  overall_status: "running" | "completed" | "failed" | "partial";
  steps_completed: number;
  steps_failed: number;
  steps_skipped: number;
  steps_running: number;
  steps_total: number;
  steps: PipelineStepLog[];
}

export interface StoreMinStockPolicyRow {
  abc: "A" | "B" | "C";
  tamano: "grande" | "mediana" | "pequena";
  min_qty: number;
}

export interface SampleSizePolicyRow {
  abc: "A" | "B" | "C";
  tamano: "grande" | "mediana" | "pequena";
  base_qty: number;
}

export interface SampleSizeCostBracketRow {
  id: number;
  name: string;
  min_cost: string | null;
  max_cost: string | null;
  factor: string;
  floor_qty: number;
  use_inverse_formula: boolean;
  constant_k: string | null;
}

export interface StoreLeadTimeRow {
  mxp_code: string;
  days: number;
}

export interface DistributionPoliciesSnapshot {
  global: Record<string, unknown>;
  min_stock_matrix: StoreMinStockPolicyRow[];
  sample_size_matrix: SampleSizePolicyRow[];
  cost_brackets: SampleSizeCostBracketRow[];
  lead_times: StoreLeadTimeRow[];
}

export const distributionService = {
  async getDistributionPolicies(): Promise<DistributionPoliciesSnapshot> {
    const res = await apiClient.get<DistributionPoliciesSnapshot>(
      `/config/distribution-policies`,
    );
    return res.data;
  },

  async setDistributionPolicy(
    key: string,
    value: unknown,
  ): Promise<{ ok: true; key: string }> {
    const res = await apiClient.put<{ ok: true; key: string }>(
      `/config/distribution-policies`,
      { key, value },
    );
    return res.data;
  },

  async setDistributionPoliciesBulk(
    policies: Record<string, unknown>,
  ): Promise<{ ok: true; updated: string[] }> {
    const res = await apiClient.put<{ ok: true; updated: string[] }>(
      `/config/distribution-policies/bulk`,
      { policies },
    );
    return res.data;
  },

  async getCoverage(productId: string): Promise<ProductCoverage> {
    const res = await apiClient.get<ProductCoverage>(
      `/compras/coverage/${encodeURIComponent(productId)}`,
    );
    return res.data;
  },

  async validatePurchaseCoverage(
    productId: string,
    proposedQty: number,
  ): Promise<ValidatePurchaseCoverageResult> {
    const res = await apiClient.post<ValidatePurchaseCoverageResult>(
      `/compras/validate-purchase-coverage`,
      { unified_product_id: productId, proposed_qty: proposedQty },
    );
    return res.data;
  },

  async getDistributionBacklog(): Promise<BacklogItem[]> {
    const res = await apiClient.get<BacklogItem[]>(
      `/compras/distribution-backlog`,
    );
    return res.data;
  },

  async getPendingDenials(): Promise<PendingDenialSummary[]> {
    const res = await apiClient.get<PendingDenialSummary[]>(
      `/compras/pending-denials`,
    );
    return res.data;
  },

  async getPoTrace(poId: string): Promise<PoTraceEntry[]> {
    const res = await apiClient.get<PoTraceEntry[]>(
      `/compras/po-trace/${encodeURIComponent(poId)}`,
    );
    return res.data;
  },

  async getHomeWidget(): Promise<HomeWidgetSnapshot> {
    const res = await apiClient.get<HomeWidgetSnapshot>(
      `/compras/home-widget`,
    );
    return res.data;
  },

  // ─────────────────────────────────────────────────────────────
  // B2.21 — Endpoints operativos de distribución (pantalla §3.3)
  // ─────────────────────────────────────────────────────────────

  async getPendingByStore(): Promise<StorePendingSummary[]> {
    const res = await apiClient.get<{
      ok: true;
      data: StorePendingSummary[];
    }>(`/distribution/pending-by-store`);
    return res.data.data;
  },

  async generateDistribution(input: {
    store?: string;
    request_id?: string;
  }): Promise<GenerateResult> {
    const res = await apiClient.post<{ ok: true; result: GenerateResult }>(
      `/distribution/generate`,
      input,
    );
    return res.data.result;
  },

  async listTransferOrders(params: {
    store?: string;
    source_type?: "seeding" | "stock_alert" | "reorder";
    status?: "draft" | "in_progress" | "received" | "cancelled";
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ total: number; items: TransferOrderListItem[] }> {
    const res = await apiClient.get<{
      ok: true;
      total: number;
      items: TransferOrderListItem[];
    }>(`/distribution/transfer-orders`, { params });
    return { total: res.data.total, items: res.data.items };
  },

  async cancelTransferOrder(
    id: string,
    body: { reason?: string } = {},
  ): Promise<CancelTransferOrderResult> {
    const res = await apiClient.post<{
      ok: true;
      data: CancelTransferOrderResult;
    }>(`/distribution/transfer-orders/${id}/cancel`, body);
    return res.data.data;
  },

  async getStoreDenials(store: string): Promise<StoreDenialItem[]> {
    const res = await apiClient.get<{ ok: true; items: StoreDenialItem[] }>(
      `/distribution/denials/${encodeURIComponent(store)}`,
    );
    return res.data.items;
  },

  // ─────────────────────────────────────────────────────────────
  // B3.26 — diffs
  // ─────────────────────────────────────────────────────────────

  async listDiffs(params: {
    store?: string;
    product_id?: string;
    reason?: string;
    auto_resolved?: boolean;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ total: number; items: TransferDiffItem[] }> {
    const q: Record<string, string | number> = {};
    if (params.store) q["store"] = params.store;
    if (params.product_id) q["product_id"] = params.product_id;
    if (params.reason) q["reason"] = params.reason;
    if (params.auto_resolved !== undefined)
      q["auto_resolved"] = String(params.auto_resolved);
    if (params.from) q["from"] = params.from;
    if (params.to) q["to"] = params.to;
    if (params.limit !== undefined) q["limit"] = params.limit;
    if (params.offset !== undefined) q["offset"] = params.offset;
    const res = await apiClient.get<{
      ok: true;
      total: number;
      items: TransferDiffItem[];
    }>(`/distribution/diffs`, { params: q });
    return { total: res.data.total, items: res.data.items };
  },

  async updateDiffReason(input: {
    id: number;
    reason: DiffReason;
    reason_detail?: string;
  }): Promise<TransferDiffItem> {
    const res = await apiClient.put<{ ok: true; diff: TransferDiffItem }>(
      `/distribution/diffs/${input.id}/reason`,
      { reason: input.reason, reason_detail: input.reason_detail },
    );
    return res.data.diff;
  },

  async getDiffsReport(params: {
    store?: string;
    product_id?: string;
    from?: string;
    to?: string;
    group_by?: "reason" | "store" | "product" | "month";
  } = {}): Promise<{ group_by: string; rows: DiffReportRow[] }> {
    const res = await apiClient.get<{
      ok: true;
      group_by: string;
      rows: DiffReportRow[];
    }>(`/distribution/diffs/report`, { params });
    return { group_by: res.data.group_by, rows: res.data.rows };
  },

  // ─────────────────────────────────────────────────────────────
  // B3.31 / B3.37 — system health + metrics
  // ─────────────────────────────────────────────────────────────

  async getSystemHealth(): Promise<SystemHealthReport> {
    const res = await apiClient.get<{
      ok: true;
      report: SystemHealthReport;
    }>(`/system/health`);
    return res.data.report;
  },

  async getSystemMetrics(): Promise<SystemMetricsReport> {
    const res = await apiClient.get<{
      ok: true;
      report: SystemMetricsReport;
    }>(`/system/metrics`);
    return res.data.report;
  },

  async getSubcompraAlerts(): Promise<{
    threshold_pct: number;
    alerts: SubcompraAlert[];
  }> {
    const res = await apiClient.get<{
      threshold_pct: number;
      alerts: SubcompraAlert[];
    }>(`/compras/subcompra-alerts`);
    return res.data;
  },

  // Trigger manual del post-odoo-sync-orchestrator (§7 plan).
  // Equivalente a lo que dispara automáticamente OdooSyncLogService.logComplete()
  // tras la migración de Odoo. Útil en dev para iterar sin re-correr migración.
  async triggerPostSyncOrchestrator(): Promise<{
    ok: true;
    result: unknown;
  }> {
    const res = await apiClient.post<{ ok: true; result: unknown }>(
      `/admin/post-sync/trigger`,
      {},
    );
    return res.data;
  },

  // Lista los últimos N runs del orquestador con sus 9 steps.
  // Consumido por DistribucionPostSyncMonitor en /odoo/production-sync.
  async getPostSyncRuns(limit = 10): Promise<PipelineRunLog[]> {
    const res = await apiClient.get<{ ok: true; runs: PipelineRunLog[] }>(
      `/system/post-sync-runs`,
      { params: { limit } },
    );
    return res.data.runs;
  },

  // Tail operacional — combina Logger live con trazas durables del post-sync.
  async getPostSyncLogsTail(
    since?: number,
    context?: string,
    limit = 500,
  ): Promise<{ lines: LogLine[]; server_ts: number }> {
    const params: Record<string, string | number> = {};
    if (since !== undefined) params["since"] = since;
    if (context) params["context"] = context;
    params["limit"] = limit;
    const res = await apiClient.get<{
      ok: true;
      lines: LogLine[];
      server_ts: number;
    }>(`/system/post-sync-logs/tail`, { params });
    return { lines: res.data.lines, server_ts: res.data.server_ts };
  },
};

export type DiffReason =
  | "no_stock_al_momento"
  | "cancelado_por_tienda"
  | "priorizado_otra_tienda"
  | "ajuste_manual_personal"
  | "error_captura_inventario"
  | "faltante_al_recibir"
  | "producto_danado"
  | "error_clasificacion"
  | "devolucion_parcial"
  | "ruta_no_completada"
  | "otro"
  | "reason_unknown";

export const DIFF_REASON_OPTIONS: DiffReason[] = [
  "no_stock_al_momento",
  "cancelado_por_tienda",
  "priorizado_otra_tienda",
  "ajuste_manual_personal",
  "error_captura_inventario",
  "faltante_al_recibir",
  "producto_danado",
  "error_clasificacion",
  "devolucion_parcial",
  "ruta_no_completada",
  "otro",
  "reason_unknown",
];

export interface TransferDiffItem {
  id: number;
  transfer_order_line_id: string;
  transfer_order_id: string;
  store_mxp_code: string;
  unified_product_id: string;
  qty_planned: string;
  qty_delivered: string;
  diff: string;
  reason: DiffReason | null;
  reason_detail: string | null;
  reason_captured_at: string | null;
  reason_captured_by: string | null;
  auto_resolved: boolean;
  created_at: string;
  done_at: string | null;
}

export interface DiffReportRow {
  group_key: string;
  count: number;
  total_diff: number;
  auto_resolved_count: number;
}

export interface SystemHealthReport {
  generated_at: string;
  last_orchestrator_run: {
    sync_run_id: string;
    started_at: string;
    status: string;
  } | null;
  last_odoo_sync: {
    sync_run_id: string;
    source: string;
    status: string;
    completed_at: string | null;
  } | null;
  denials_by_status: Record<string, number>;
  transfer_orders_by_status: Record<string, number>;
  diffs_last_30d: {
    total: number;
    auto_resolved: number;
    auto_resolution_rate: number;
    pending_manual_capture: number;
  };
  samples: {
    pending_evaluation_orders: number;
    exclusions_active: number;
  };
  picking_errors_unresolved: number;
  alerts: string[];
}

export interface SubcompraAlert {
  store_mxp_code: string;
  unified_product_id: string;
  total_diffs_30d: number;
  no_stock_count_30d: number;
  no_stock_pct: number;
  last_occurrence: string;
  recommended_action: string;
}

export interface SystemMetricsReport {
  generated_at: string;
  kpis: {
    backlog_vigente: number;
    backlog_expirado_acumulado: number;
    denegaciones_cumplidas_ratio: number;
    fill_rate_by_store: Array<{ store: string; fill_rate: number }>;
    no_stock_al_momento_pct_30d: number;
    tiempo_ciclo_promedio_dias: number;
    cobertura_dci_promedio: number;
    ventas_vs_cobertura_correlation: number | null;
    auto_resolucion_diffs_pct_30d: number;
  };
}

export interface StorePendingSummary {
  store_mxp_code: string;
  display_name: string;
  lines_count: number;
  total_units: number;
  total_value: number;
  active_denials_count: number;
  oldest_denial_days: number;
  criticality_score: number;
  priority_level: "critical" | "alert" | "normal";
}

export interface GenerateStoreResult {
  request_id: string;
  store_mxp_code: string;
  picking_ids: number[];
  transfer_order_ids: string[];
  skipped_lines: Array<{ unified_product_id: string; reason: string }>;
  denials_created: number;
  errors: Array<{ origin: string; message: string }>;
}

export interface GenerateBatchResult {
  request_id: string;
  per_store: GenerateStoreResult[];
  total_picking_ids: number[];
  total_denials_created: number;
  total_errors: number;
}

export type GenerateResult = GenerateStoreResult | GenerateBatchResult;

export interface CancelTransferOrderResult {
  ok: true;
  order_id: string;
  reason: string;
  status_before: string;
  recos_reverted: number;
  already_terminal: boolean;
}

export interface TransferOrderListItem {
  id: string;
  odoo_picking_id: number | null;
  source_type: "seeding" | "stock_alert" | "reorder";
  store_mxp_code: string;
  origin_warehouse: string;
  status: "draft" | "in_progress" | "received" | "cancelled";
  request_id: string;
  created_at: string;
  promoted_at: string | null;
  received_at: string | null;
}

export interface StoreDenialItem {
  id: number;
  unified_product_id: string;
  qty_denied: number;
  qty_requested: number;
  source_type: string;
  status: string;
  denial_date: string;
  age_days: number;
  is_valid: boolean;
  invalid_reason: string | null;
}
